import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { Upload } from "lucide-react";
import { toast } from "sonner";

const BUILTIN = [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["stage", "Stage"], ["owner", "Owner"], ["gmv", "GMV"], ["event_date", "Event date"], ["source", "Source"], ["qualification", "Qualified"], ["created_at", "Created time (original)"]];

export function parseCsv(text) {
  const out = []; let row = []; let cur = ""; let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"' && text[i + 1] === '"') { cur += '"'; i++; } else if (c === '"') q = false; else cur += c; }
    else if (c === '"') q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cur); out.push(row); row = []; cur = ""; }
    else cur += c;
  }
  if (cur || row.length) { row.push(cur); out.push(row); }
  const [head, ...body] = out.filter((r) => r.some((x) => x.trim()));
  return { headers: head || [], rows: body.map((r) => Object.fromEntries((head || []).map((h, i) => [h, r[i] ?? ""]))) };
}

const guess = (h, fields) => {
  const n = h.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (["createdtime", "createdat", "createddate", "leadcreatedtime", "submittedat"].includes(n)) return "created_at";
  const all = [...BUILTIN, ...fields.map((f) => [f.key, f.label])];
  return (all.find(([k, l]) => k.replace(/_/g, "") === n || l.toLowerCase().replace(/[^a-z0-9]/g, "") === n) || [""])[0];
};

export default function ImportTool() {
  const { confirm } = useConfirm();
  const [fields, setFields] = useState([]);
  const [sops, setSops] = useState([]);
  const [csv, setCsv] = useState(null);
  const [mapping, setMapping] = useState({});
  const [opt, setOpt] = useState({ sop_id: "", track: "", step: 0 });
  const [res, setRes] = useState(null);
  useEffect(() => { setRes(null); }, [mapping, opt]);
  useEffect(() => {
    api.get("/crm/config").then((r) => setFields((r.data.fields || []).filter((f) => !BUILTIN.some(([k]) => k === f.key))));
    api.get("/crm/sops").then((r) => setSops(r.data.rows));
  }, []);
  const onFile = (f) => {
    if (!f) return;
    f.text().then((t) => { const p = parseCsv(t); setCsv(p); setMapping(Object.fromEntries(p.headers.map((h) => [h, guess(h, fields)]))); setRes(null); });
  };
  const sop = sops.find((s) => s._id === opt.sop_id);
  const run = async (dry) => {
    if (!dry && !(await confirm({ title: `Import ${csv.rows.length} rows?`, body: "This writes leads into the CRM.", okLabel: "Import" }))) return;
    try { const { data } = await api.post("/crm/import", { rows: csv.rows, mapping, ...opt, sop_id: opt.sop_id || null, track: opt.track || null, dry_run: dry }, { timeout: 120000 }); setRes(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  return (
    <div className="space-y-4 max-w-4xl" data-testid="import-tool">
      <div className="text-xs text-neutral-500">Upload a CSV, map columns to CRM fields, then run a dry run. Leads are de-duplicated by email or phone (existing leads are updated). Map Zoho's <b>Created Time</b> to "Created time (original)" so weekly numbers use the real lead date, not the import date. Re-importing the same file with it mapped fixes dates on leads already imported.</div>
      <label className="btn w-fit cursor-pointer" data-testid="import-file-label"><Upload size={13} />Choose CSV<input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="import-file-input" /></label>
      {csv && <>
        <div className="panel p-3 space-y-1.5" data-testid="import-mapping">
          <div className="text-sm font-medium mb-1">{csv.rows.length} rows · map columns</div>
          {csv.headers.map((h) => (
            <div key={h} className="grid grid-cols-[1fr_20px_1fr_1fr] gap-2 items-center text-xs">
              <span className="truncate">{h}</span><span className="text-neutral-600">→</span>
              <select className="px-2 py-1 rounded border" value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} data-testid={`import-map-${h}`}>
                <option value="">(skip)</option>{BUILTIN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}{fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <span className="truncate text-neutral-500">{csv.rows[0]?.[h]}</span>
            </div>
          ))}
        </div>
        <div className="panel p-3 flex flex-wrap gap-2 items-center text-xs">
          Assign SOP <select className="px-2 py-1 rounded border" value={opt.sop_id} onChange={(e) => setOpt({ sop_id: e.target.value, track: "", step: 0 })} data-testid="import-sop"><option value="">none</option>{sops.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}</select>
          {sop && <>current step <select className="px-2 py-1 rounded border" value={opt.track} onChange={(e) => setOpt({ ...opt, track: e.target.value, step: 0 })} data-testid="import-track"><option value="">(none, assign only)</option>{Object.entries(sop.tracks).map(([k, t]) => <option key={k} value={k}>{t.name || k}</option>)}</select>
            {opt.track && <select className="px-2 py-1 rounded border" value={opt.step} onChange={(e) => setOpt({ ...opt, step: Number(e.target.value) })} data-testid="import-step">{sop.tracks[opt.track].steps.map((s, i) => <option key={i} value={i}>{i + 1}. {s.label}</option>)}</select>}</>}
          <button className="btn ml-auto" onClick={() => run(true)} data-testid="import-dry-run-btn">Dry run</button>
          <button className="btn btn-primary" disabled={!res?.dry_run} onClick={() => run(false)} data-testid="import-run-btn">Run import</button>
        </div>
      </>}
      {res && (
        <div className="panel p-3 space-y-2" data-testid="import-result">
          <div className="text-sm">{res.dry_run ? "Dry run" : "Imported"}: <b>{res.summary.create}</b> new · <b>{res.summary.update}</b> update · <b>{res.summary.skip}</b> skipped</div>
          <div className="max-h-64 overflow-y-auto text-xs">{res.rows.map((r) => (
            <div key={r.row} className="flex gap-3 py-0.5 border-b border-[var(--border)]"><span className="w-10 text-neutral-500">#{r.row}</span><span className={`w-14 ${r.action === "skip" ? "text-[var(--breach)]" : r.action === "update" ? "text-amber-400" : "text-emerald-400"}`}>{r.action}</span><span className="flex-1 truncate">{r.name}</span><span className="text-neutral-500">{r.reason || r.email}</span></div>
          ))}</div>
        </div>
      )}
    </div>
  );
}
